/*
 * How a church's website opens (ADR 0073, amended 2026-10-14 twice): nothing, a short dark screen with a spinner, or
 * the church's own welcome words. Stored in `tenant_settings.branding.intro`, carried by `private.site_brand`, handed
 * to a theme's chrome as `ThemeChromeProps.intro`; only a theme that draws an intro (Revival) reads it.
 *
 * The words are the church's, never the theme's: a first line ("WELCOME"), the letters in the round mark, and the
 * words the second line cycles through before it lands on the last one. The holds (how long each word stays, in
 * seconds) and the shorter list for a phone are optional; the theme fills them in. Everything is bounded, because a
 * church that types a paragraph gets an intro nobody waits for: up to eight words of up to 24 characters, each held
 * between a tenth of a second and a second and a half.
 */

import { z } from "zod";

export const INTRO_STYLES = ["spinner", "welcome"] as const;
export type IntroStyle = (typeof INTRO_STYLES)[number];

export const INTRO_STYLE_LABELS: Record<IntroStyle, string> = {
  spinner: "A dark screen with a small spinner",
  welcome: "Your welcome words",
};

/** How many words the second line may cycle through, and how long each may be. */
export const INTRO_MAX_WORDS = 8;
export const INTRO_MAX_WORD_LENGTH = 24;
export const INTRO_MAX_MARK_LENGTH = 4;
/** How long one word may stay on screen, in seconds. */
export const INTRO_MIN_HOLD = 0.1;
export const INTRO_MAX_HOLD = 1.5;

const word = z.string().trim().min(1).max(INTRO_MAX_WORD_LENGTH);
const words = z.array(word).min(1).max(INTRO_MAX_WORDS);
const hold = z.number().min(INTRO_MIN_HOLD).max(INTRO_MAX_HOLD);
const holds = z.array(hold).min(1).max(INTRO_MAX_WORDS);

export const welcomeIntroSchema = z.object({
  style: z.literal("welcome"),
  /** The first line, as the church writes it ("WELCOME"). Drawn in capitals whatever the case typed. */
  line: word,
  /** The words the second line cycles through, in order, the last one landing ("TO LIFE", …, "HOME"). */
  words,
  /** Seconds each word stays; one per word. Absent, the theme holds each briefly and the last a little longer. */
  holds: holds.optional(),
  /** A shorter list for a phone, where a long cycle keeps a visitor waiting. Absent, the theme shortens `words`. */
  mobileWords: words.optional(),
  mobileHolds: holds.optional(),
  /** The one word a returning visitor sees in the short version. Absent, the last of `words`. */
  finalWord: word.optional(),
  /** The letters in the round mark beside the cycling word ("ZOE"). Absent, the church's initials. */
  mark: z.string().trim().min(1).max(INTRO_MAX_MARK_LENGTH).optional(),
});

export const spinnerIntroSchema = z.object({ style: z.literal("spinner") });

export type WelcomeIntro = z.infer<typeof welcomeIntroSchema>;
export type SpinnerIntro = z.infer<typeof spinnerIntroSchema>;
/** An intro the church asked for: the spinner, or its welcome words. `false` is none. */
export type SiteIntro = SpinnerIntro | WelcomeIntro;

/**
 * What `branding.intro` may hold. The first form was a boolean (`true` = the spinner), and a church that stored one
 * keeps its meaning: `true` reads as the spinner, `false` and nothing as off.
 */
export const introSchema = z.preprocess(
  (value) => {
    if (value === true) return { style: "spinner" };
    if (value === undefined || value === null) return false;
    return value;
  },
  z.union([z.literal(false), spinnerIntroSchema, welcomeIntroSchema]),
);

export type IntroSetting = z.infer<typeof introSchema>;

/**
 * The second line's words and holds as the theme plays them, every optional part filled in. The defaults follow the
 * owner's reference: each word stays about a quarter of a second and the last one a little longer, and a phone sees
 * the first two words and the last when the list is longer than three.
 */
export interface IntroPlan {
  readonly line: string;
  readonly mark: string;
  readonly words: readonly string[];
  readonly holds: readonly number[];
  readonly mobileWords: readonly string[];
  readonly mobileHolds: readonly number[];
  readonly finalWord: string;
}

const DEFAULT_HOLD = 0.26;
const DEFAULT_LAST_HOLD = 0.42;
const DEFAULT_LAST_HOLD_MOBILE = 0.4;

function fillHolds(count: number, given: readonly number[] | undefined, last: number): number[] {
  return Array.from({ length: count }, (_, i) => {
    const own = given?.[i];
    if (own !== undefined) return own;
    return i === count - 1 ? last : DEFAULT_HOLD;
  });
}

/**
 * The letters a church is known by when it names none: the first letter of each word of its name, up to three,
 * in capitals ("Hope Church Northside" → "HCN"; "Zoe" → "Z"). A name with no letters gives an empty mark, and the
 * theme draws the circle alone.
 */
export function initialsOf(siteName: string): string {
  return siteName
    .split(/[\s\-–—]+/)
    .map((part) => part.replace(/[^\p{L}\p{N}]/gu, "").charAt(0))
    .filter((c) => c.length > 0)
    .slice(0, 3)
    .join("")
    .toUpperCase();
}

export function introPlan(intro: WelcomeIntro, siteName: string): IntroPlan {
  const upper = (text: string) => text.toUpperCase();
  const words = intro.words.map(upper);
  const last = words[words.length - 1] ?? "";
  const mobileWords =
    intro.mobileWords?.map(upper) ??
    (words.length > 3 ? [words[0] ?? "", words[1] ?? "", last] : words);
  return {
    line: upper(intro.line),
    mark: intro.mark === undefined ? initialsOf(siteName) : upper(intro.mark),
    words,
    holds: fillHolds(words.length, intro.holds, DEFAULT_LAST_HOLD),
    mobileWords,
    mobileHolds: fillHolds(
      mobileWords.length,
      intro.mobileWords === undefined ? undefined : intro.mobileHolds,
      DEFAULT_LAST_HOLD_MOBILE,
    ),
    finalWord: intro.finalWord === undefined ? last : upper(intro.finalWord),
  };
}
